// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/components/admin/AdminEditProfileButton.js
//
// Édition d'une fiche membre PAR UN ADMIN — 2026-09-15. Rendu uniquement là
// où `isAdmin === true` (le composant parent doit faire ce gating avant de
// monter, même convention que AmbassadorAdminToggle/SchoolRolesAdminEditor).
// Le backend re-vérifie de toute façon (checkRole admin) : 403 sinon.
//
// ── WHITELIST STRICTE, MIROIR EXACT DU BACKEND ─────────────────────────────
// Uniquement les champs de contact/annuaire déjà éditables en self-edit
// (routes/member.js FIELD_LIMITS) + career_status/sector. JAMAIS mot de
// passe, confidentialité, consentements/géoloc, rôles/member_type/avatar —
// ces leviers ont leurs propres UI admin dédiées (AmbassadorAdminToggle,
// SchoolRolesAdminEditor...) et ce composant ne les touche pas. Si un jour
// cette liste doit s'étendre, l'étendre D'ABORD côté route (PUT
// /:id/admin-profile), jamais l'inverse — le frontend ne doit jamais pouvoir
// envoyer un champ que le backend ignorerait silencieusement.
//
// Seuls les champs RÉELLEMENT modifiés partent dans la requête (toUpdatePayload,
// utils/formDiff.js) — même mécanique que FundraisingSettingsSection (identité
// légale), qui a le même besoin de partial-update.
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CAlert,
  CSpinner,
  CRow,
  CCol,
  CFormLabel,
  CFormInput,
  CFormTextarea,
  CFormSelect,
} from '@coreui/react'
import { get, put } from '../../utils/apiClient'
import { toUpdatePayload } from '../../utils/formDiff'
import { formatDateI18n } from '../../utils/i18nFormatters'
import { CAREER_STATUS_VALUES, getCareerStatusLabel } from '../../utils/careerStatus'
import { SECTOR_VALUES, getSectorLabel } from '../../utils/sectors'

const STRING_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'mobilePhone',
  'city',
  'department',
  'country',
  'company',
  'position',
  'linkedin',
]

function buildForm(member) {
  const form = {}
  for (const field of STRING_FIELDS) form[field] = member?.[field] || ''
  form.notes = member?.notes || ''
  form.career_status = member?.career_status || 'not_specified'
  form.sector = member?.sector || ''
  return form
}

const AdminEditProfileButton = ({ member, onUpdate }) => {
  const { t, i18n } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(() => buildForm(member))
  const [initial, setInitial] = useState(() => buildForm(member))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastEdit, setLastEdit] = useState(null)
  const [lastEditLoading, setLastEditLoading] = useState(false)

  const openModal = () => {
    const fresh = buildForm(member)
    setForm(fresh)
    setInitial(fresh)
    setError(null)
    setShowModal(true)

    setLastEditLoading(true)
    get(`/members/${member.id}/admin-profile-log`)
      .then((res) => setLastEdit(res?.lastEdit || null))
      .catch(() => setLastEdit(null))
      .finally(() => setLastEditLoading(false))
  }

  const closeModal = () => {
    if (loading) return
    setShowModal(false)
  }

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSave = async () => {
    setLoading(true)
    setError(null)
    try {
      const payload = toUpdatePayload(form, initial)
      if (Object.keys(payload).length === 0) {
        setShowModal(false)
        return
      }
      const res = await put(`/members/${member.id}/admin-profile`, payload)
      if (onUpdate) {
        // Le backend ne renvoie que les champs modifiés (res.changes) — on
        // reconstruit un objet member-like à partir du FORM local (déjà
        // normalisé côté serveur pour les cas simples ; suffisant pour un
        // refresh d'affichage immédiat, le prochain fetch complet réconciliera).
        const patch = {}
        for (const field of Object.keys(payload)) patch[field] = form[field]
        onUpdate(patch)
      }
      setShowModal(false)
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          t('forms:member_card.admin_edit_error_generic'),
      )
    } finally {
      setLoading(false)
    }
  }

  const renderField = (field, labelKey, colSize = 6) => (
    <CCol md={colSize} className="mb-3">
      <CFormLabel>{t(labelKey)}</CFormLabel>
      <CFormInput value={form[field]} onChange={handleFieldChange(field)} disabled={loading} />
    </CCol>
  )

  return (
    <>
      <CButton
        color="light"
        size="sm"
        onClick={openModal}
        title={t('forms:member_card.admin_edit_tooltip')}
        style={{ zIndex: 10, position: 'relative' }}
      >
        {t('forms:member_card.admin_edit_button')}
      </CButton>

      <CModal visible={showModal} onClose={closeModal} backdrop="static" size="lg">
        <CModalHeader>
          <CModalTitle>
            {t('forms:member_card.admin_edit_modal_title', {
              name: `${member.firstName} ${member.lastName}`,
            })}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {error && (
            <CAlert color="danger" className="py-2 mb-3" style={{ fontSize: '0.9rem' }}>
              {error}
            </CAlert>
          )}

          {!lastEditLoading && lastEdit && (
            <CAlert color="info" className="py-2 mb-3" style={{ fontSize: '0.85rem' }}>
              {t('forms:member_card.admin_edit_last_edit', {
                name: lastEdit.adminName || t('forms:member_card.admin_edit_unknown_admin'),
                date: formatDateI18n(lastEdit.createdAt, 'PPp', i18n.language),
              })}
            </CAlert>
          )}

          <CRow>
            {renderField('firstName', 'forms:member_card.admin_edit_field_first_name')}
            {renderField('lastName', 'forms:member_card.admin_edit_field_last_name')}
            {renderField('email', 'forms:member_card.admin_edit_field_email')}
            {renderField('phone', 'forms:member_card.admin_edit_field_phone')}
            {renderField('mobilePhone', 'forms:member_card.admin_edit_field_mobile_phone')}
            {renderField('city', 'forms:member_card.admin_edit_field_city')}
            {renderField('department', 'forms:member_card.admin_edit_field_department')}
            {renderField('country', 'forms:member_card.admin_edit_field_country')}
            {renderField('company', 'forms:member_card.admin_edit_field_company')}
            {renderField('position', 'forms:member_card.admin_edit_field_position')}
            {renderField('linkedin', 'forms:member_card.admin_edit_field_linkedin', 12)}

            <CCol md={6} className="mb-3">
              <CFormLabel>{t('forms:member_card.admin_edit_field_career_status')}</CFormLabel>
              <CFormSelect
                value={form.career_status}
                onChange={handleFieldChange('career_status')}
                disabled={loading}
              >
                {CAREER_STATUS_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {getCareerStatusLabel(value, t)}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol md={6} className="mb-3">
              <CFormLabel>{t('forms:member_card.admin_edit_field_sector')}</CFormLabel>
              <CFormSelect
                value={form.sector}
                onChange={handleFieldChange('sector')}
                disabled={loading}
              >
                <option value="">{t('forms:member_card.admin_edit_field_sector_none')}</option>
                {SECTOR_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {getSectorLabel(value, t)}
                  </option>
                ))}
              </CFormSelect>
            </CCol>

            <CCol md={12} className="mb-3">
              <CFormLabel>{t('forms:member_card.admin_edit_field_notes')}</CFormLabel>
              <CFormTextarea
                rows={3}
                value={form.notes}
                onChange={handleFieldChange('notes')}
                disabled={loading}
              />
            </CCol>
          </CRow>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={closeModal} disabled={loading}>
            {t('forms:member_card.admin_edit_cancel')}
          </CButton>
          <CButton color="primary" onClick={handleSave} disabled={loading}>
            {loading ? <CSpinner size="sm" /> : t('forms:member_card.admin_edit_save')}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default AdminEditProfileButton
